/* eslint-disable no-extra-boolean-cast */
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';
import {
  setAddress,
  setPlaceId,
  setPosition,
  setProductId,
  setSelectedShop,
} from '../actions/course';

import Separator from '../components/Separator';
import Box from '../components/shared/atoms/Box';
import Container from '../components/shared/atoms/Container';
import FrequentlyAskedQuestion from '../components/workshopStandalone/FrequentlyAskedQuestion';
import Header from '../components/workshopStandalone/Header';
import InformationsWorkshop from '../components/workshopStandalone/InformationsWorkshop';
import Maps from '../components/workshopStandalone/Maps';
import ReservationBox from '../components/workshopStandalone/ReservationBox';
import SectionInformations from '../components/workshopStandalone/SectionInformations';
import * as api from '../utils/api';
import { parse_all_shops } from '../utils/utils';
import Danger24 from '../components/icons/Danger24';
import Calendar24 from '../components/icons/Calendar24';
import Text from '../components/shared/atoms/Text';
import ToolKitFix from '../components/shared/ToolkitFix';

const StyledText = styled(Text)`
  z-index: 2;
`;

const Spring = styled(ToolKitFix)`
  width: 100px;
  height: 100px;
  bottom: -10px;
  right: 70px;
  transform: rotate(340deg);
  position: absolute;
  z-index: 1;
  @media (max-width: ${(p) => p.theme.breakpoints.sm}) {
    display: none;
  }
`;
const Hexa = styled(ToolKitFix)`
  width: 100px;
  height: 100px;
  bottom: -10px;
  right: -20px;
  transform: rotate(340deg);
  position: absolute;
  @media (max-width: ${(p) => p.theme.breakpoints.sm}) {
    display: none;
  }
`;

export default function WorkshopStandalone() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState({});
  const refMaps = useRef(null);
  const refHours = useRef(null);
  const [product, setProduct] = useState('1');
  const { id, name } = useParams();

  const getShop = async ({ id }) => {
    setLoading(true);
    const shopItem = await api.get(`/shops/${id}`);
    const parsed = parse_all_shops({ shopItem });
    setShop({ ...parsed[0].value });
    setLoading(false);
  };

  useEffect(() => {
    getShop({ id });
  }, [id]);

  const scrollToRef = (index) => {
    const refScroll =
      index === '2'
        ? refMaps.current.offsetTop - 80
        : refHours.current.offsetTop - 80;

    window.scrollTo({
      top: refScroll,
      behavior: 'smooth',
    });
  };

  const createAppointment = () => {
    dispatch(setPlaceId(''));
    dispatch(setAddress(shop.address));
    dispatch(setPosition(shop.address, ''));
    dispatch(setProductId(product));
    dispatch(setSelectedShop(shop));
    history.push(`/reparation/symptoms`);
  };

  const BookableOnly = ({ children }) =>
    shop && shop.bookable ? (
      <React.Fragment>{children}</React.Fragment>
    ) : (
      <React.Fragment />
    );

  return (
    <Container fluid>
      <Helmet
        title={name}
        meta={[
          {
            name: 'description',
            content: t('standalone.meta.description', { name }),
          },
        ]}
      />
      <Container pt={4}>
        <Header loading={loading} {...shop} scrollToRef={scrollToRef} />
        <BookableOnly>
          <Box flexDirection={{ xs: 'column', md: 'row' }} mb={7}>
            <div>
              <Box
                flex={5}
                p={5}
                mr={{ xs: 0, md: 4 }}
                bg="gray.lightest"
                borderRadius="xs"
                style={{ position: 'relative', overflow: 'hidden' }}>
                <Spring name="spring" color="yellow.main" />
                <Hexa name="hexa" color="violet.pale" />
                <Box>
                  <StyledText
                    mb={4}
                    color="black"
                    fontWeight="bold"
                    variant="large">
                    {t('standalone.section.information.title')}
                  </StyledText>
                  <Box flexDirection="row">
                    <Danger24 color="gray.darker" />
                    <StyledText mb={4} color="black" variant="medium" ml={2}>
                      {t('standalone.section.information.text.1')}
                    </StyledText>
                  </Box>
                  <Box flexDirection="row">
                    <Calendar24 color="gray.darker" />
                    <StyledText mb={4} color="black" variant="medium" ml={2}>
                      {t('standalone.section.information.text.2')}
                    </StyledText>
                  </Box>
                  <Box flexDirection="row">
                    <Danger24 color="gray.darker" />
                    <StyledText mb={4} color="black" variant="medium" ml={2}>
                      {t('standalone.section.information.text.3')}
                    </StyledText>
                  </Box>
                </Box>
              </Box>
            </div>
            <ReservationBox
              {...shop}
              createAppointment={createAppointment}
              product={product}
              setProduct={setProduct}
            />
          </Box>
        </BookableOnly>
        <Separator axe="horizontal" my={2} />
        <div ref={refMaps}>
          <Maps {...shop} />
        </div>
        <BookableOnly>
          <Separator axe="horizontal" my={2} />
          <SectionInformations
            {...shop}
            product={product}
            setProduct={setProduct}
          />
        </BookableOnly>
        <Separator axe="horizontal" my={2} />
        <div ref={refHours}>
          <InformationsWorkshop
            {...shop}
            loading={loading}
            setProduct={setProduct}
            product={product}
            createAppointment={createAppointment}
            bookable={shop && shop.bookable}
          />
        </div>
        <Separator axe="horizontal" my={2} />
        <FrequentlyAskedQuestion />
      </Container>
    </Container>
  );
}
